/**
 * Enrolement du poste (design, section 4.1).
 *
 * 1. La cle de signature est creee (ou reprise si un essai precedent a
 *    echoue) et gardee dans `meta` AVANT l'appel : un poste n'a qu'une cle,
 *    meme apres plusieurs essais.
 * 2. POST /caisse/terminals/activate avec le code, le nom et la cle publique.
 * 3. Le poste, son magasin et son jeton sont ecrits dans UNE transaction.
 *
 * Le jeton ne va que dans IndexedDB : jamais localStorage, sessionStorage ni
 * cookie, et jamais dans un message ou un journal.
 */
import { getMeta, getMetaMany, putMeta } from '../store/meta';
import { META_KEYS } from '../store/schema';
import { generateDeviceKey } from './deviceKey';

// Meme alphabet que le serveur (CaisseTerminalService) : sans I, O, 0 ni 1.
export const ACTIVATION_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{8}$/;
export const NAME_MAX_LENGTH = 100;

export class EnrollmentError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'EnrollmentError';
        this.code = code;
    }
}

/** Ce que l'employe tape, ramene a l'alphabet du code (majuscules, 8 au plus). */
export function cleanActivationCode(raw) {
    return String(raw || '').toUpperCase().replace(/[^A-HJ-NP-Z2-9]/g, '').slice(0, 8);
}

/** Cle de signature du poste : celle deja gardee, sinon une nouvelle. */
export async function getOrCreateSigningKey(db, subtle) {
    const existing = await getMeta(db, META_KEYS.SIGNING_KEY);
    if (existing && existing.private_key && existing.public_jwk) return existing;
    const key = await generateDeviceKey(subtle);
    const record = { ...key, created_at: new Date().toISOString() };
    await putMeta(db, { [META_KEYS.SIGNING_KEY]: record });
    return record;
}

/**
 * @param {object} options
 * @param {IDBDatabase} options.db
 * @param {{ activateTerminal: Function }} options.api
 * @param {string} options.code
 * @param {string} options.name
 * @param {SubtleCrypto} [options.subtle]
 * @returns {Promise<object>} le poste enregistre (sans le jeton)
 */
export async function enrollTerminal({ db, api, code, name, subtle }) {
    const normalizedCode = String(code || '').trim().toUpperCase();
    if (!ACTIVATION_CODE_PATTERN.test(normalizedCode)) {
        throw new EnrollmentError('Code d\'activation mal forme', 'CODE_FORMAT');
    }
    const terminalName = String(name || '').trim();
    if (!terminalName) throw new EnrollmentError('Nom du poste requis', 'NOM_REQUIS');
    if (terminalName.length > NAME_MAX_LENGTH) throw new EnrollmentError('Nom du poste trop long', 'NOM_TROP_LONG');

    const current = await getMeta(db, META_KEYS.TERMINAL);
    if (current) throw new EnrollmentError('Ce poste est deja active', 'DEJA_ACTIVE');

    const key = await getOrCreateSigningKey(db, subtle);
    const result = await api.activateTerminal({ code: normalizedCode, name: terminalName, publicKeyJwk: key.public_jwk });

    const enrolledAt = new Date().toISOString();
    const terminal = {
        terminal_id: result.terminal_id,
        name: terminalName,
        store_id: result.store.store_id,
        store_name: result.store.name || null,
        store: result.store,
        config: result.config,
        enrolled_at: enrolledAt,
    };
    await putMeta(db, {
        [META_KEYS.TERMINAL]: terminal,
        [META_KEYS.DEVICE_TOKEN]: result.device_token,
        [META_KEYS.TERMINAL_STATUS]: { status: 'ACTIVE', checked_at: enrolledAt },
    });
    return terminal;
}

/**
 * Etat du poste au demarrage, lu en une fois.
 * @returns {Promise<{ terminal: object|null, deviceToken: string|null, status: string|null, hasSigningKey: boolean }>}
 */
export async function loadTerminalState(db) {
    const values = await getMetaMany(db, [
        META_KEYS.TERMINAL, META_KEYS.DEVICE_TOKEN, META_KEYS.TERMINAL_STATUS, META_KEYS.SIGNING_KEY,
    ]);
    const statusRecord = values[META_KEYS.TERMINAL_STATUS];
    const key = values[META_KEYS.SIGNING_KEY];
    return {
        terminal: values[META_KEYS.TERMINAL] || null,
        deviceToken: values[META_KEYS.DEVICE_TOKEN] || null,
        status: statusRecord ? statusRecord.status : null,
        hasSigningKey: Boolean(key && key.private_key),
    };
}

/** Garde le dernier statut connu du poste (ACTIVE, DISABLED, REVOKED). */
export async function saveTerminalStatus(db, status) {
    await putMeta(db, { [META_KEYS.TERMINAL_STATUS]: { status, checked_at: new Date().toISOString() } });
}

const ERP_PATH = 'Magasin → Caisse de secours → Postes';

/** Message a afficher pour une erreur d'enrolement : quoi faire, pas ce qui s'est passe. */
export function enrollmentErrorMessage(error) {
    const code = error && error.code;
    switch (code) {
        case 'CODE_FORMAT':
            return 'Le code d\'activation a 8 caractères : lettres et chiffres.';
        case 'NOM_REQUIS':
            return 'Entrez le nom du poste, par exemple « Caisse 1 ».';
        case 'NOM_TROP_LONG':
            return `Le nom du poste ne doit pas dépasser ${NAME_MAX_LENGTH} caractères.`;
        case 'DEJA_ACTIVE':
            return 'Ce poste est déjà activé.';
        case 'CODE_INVALIDE':
            return `Code inconnu. Vérifiez le code affiché dans l'ERP : ${ERP_PATH}.`;
        case 'CODE_EXPIRE':
            return `Ce code est expiré. Créez-en un nouveau dans l'ERP : ${ERP_PATH}.`;
        case 'CODE_DEJA_UTILISE':
            return `Ce code a déjà servi. Créez-en un nouveau dans l'ERP : ${ERP_PATH}.`;
        case 'MAGASIN_INACTIF':
            return 'Le magasin de ce code est inactif dans l\'ERP. Appelez le responsable.';
        default:
            break;
    }
    const kind = error && error.kind;
    if (kind === 'network' || kind === 'timeout') {
        return 'Pas de connexion à l\'ERP. L\'activation demande Internet : vérifiez la connexion, puis réessayez.';
    }
    if (kind === 'http' && error.status === 429) {
        return 'Trop d\'essais. Attendez quelques minutes, puis réessayez.';
    }
    return 'L\'activation n\'a pas abouti. Réessayez ; si le problème continue, appelez le responsable.';
}
